// График «Рекламы»: расход столбцами, выплаты за апрувы линией, пунктир — с прогнозом по лидам в обработке.
// Точки — дни; длинный период сводится по неделям (больше 35 дней) и по месяцам (больше полугода), как в Финансах.
// Цифры дней считает api; недели и месяцы — просто суммы дней (цен лида и апрува в подсказке у них нет).
import { dayLabel } from '@autoaff/schemas/period';
import { Card, DayChart, int, signedUsd, usd } from '@autoaff/ui';
import type { DayRow } from '../../business';

type Grain = 'day' | 'week' | 'month';

interface Point {
  /** Первый день точки (ГГГГ-ММ-ДД). */
  day: string;
  /** Последний день точки. */
  until: string;
  spend: number;
  leads: number;
  approved: number;
  inProcess: number;
  revenue: number;
  expected: number;
  profit: number;
  forecastProfit: number;
  cpl: number | null;
}

export function grainOf(days: number): Grain {
  return days > 183 ? 'month' : days > 35 ? 'week' : 'day';
}

/** Понедельник недели дня. */
function monday(day: string): string {
  const d = new Date(`${day}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() - ((d.getUTCDay() + 6) % 7));
  return d.toISOString().slice(0, 10);
}

const r2 = (n: number) => Math.round(n * 100) / 100;

function group(days: DayRow[], grain: Grain): Point[] {
  if (grain === 'day')
    return days.map((x) => ({
      day: x.day,
      until: x.day,
      spend: x.spend,
      leads: x.leads,
      approved: x.approved,
      inProcess: x.inProcess,
      revenue: x.revenue,
      expected: x.expected,
      profit: x.profit,
      forecastProfit: x.forecastProfit,
      cpl: x.cpl,
    }));
  const out = new Map<string, Point>();
  for (const x of days) {
    const key = grain === 'week' ? monday(x.day) : `${x.day.slice(0, 7)}-01`;
    const p = out.get(key);
    out.set(
      key,
      p
        ? {
            ...p,
            until: x.day,
            spend: r2(p.spend + x.spend),
            leads: p.leads + x.leads,
            approved: p.approved + x.approved,
            inProcess: p.inProcess + x.inProcess,
            revenue: r2(p.revenue + x.revenue),
            expected: r2(p.expected + x.expected),
            profit: r2(p.profit + x.profit),
            forecastProfit: r2(p.forecastProfit + x.forecastProfit),
          }
        : {
            day: x.day,
            until: x.day,
            spend: x.spend,
            leads: x.leads,
            approved: x.approved,
            inProcess: x.inProcess,
            revenue: x.revenue,
            expected: x.expected,
            profit: x.profit,
            forecastProfit: x.forecastProfit,
            cpl: null,
          },
    );
  }
  return [...out.values()];
}

export function AdsChart({ days }: { days: DayRow[] }) {
  const grain = grainOf(days.length);
  const points = group(days, grain);
  const byDay = new Map(points.map((p) => [p.day, p]));
  const title =
    grain === 'day'
      ? 'Расход и выплаты по дням'
      : grain === 'week'
        ? 'Расход и выплаты по неделям'
        : 'Расход и выплаты по месяцам';
  const label = (day: string) => {
    const p = byDay.get(day);
    // «сен», «дек 2025»
    if (grain === 'month') return dayLabel(day).split(' ').slice(1).join(' ');
    if (grain === 'week' && p && p.until !== p.day) {
      const a = dayLabel(p.day);
      const b = dayLabel(p.until);
      return a.split(' ')[1] === b.split(' ')[1] ? `${a.split(' ')[0]}–${b}` : `${a} — ${b}`;
    }
    return dayLabel(day);
  };

  return (
    <Card title={title} className="ads-chart">
      <div className="ads-chart-box">
        <DayChart
          height={200}
          maxBar={grain === 'day' ? 36 : 28}
          xLabel={grain === 'day' ? undefined : label}
          points={points.map((p) => ({
            day: p.day,
            bar: p.spend,
            line: p.revenue,
            dashed: r2(p.revenue + p.expected),
          }))}
          labels={{ bar: 'Расход', line: 'Выплаты', dashed: 'С прогнозом' }}
          format={(n) => usd(n)}
          tooltip={(pt) => {
            const p = byDay.get(pt.day);
            if (!p) return null;
            return (
              <>
                <span>Расход: {usd(p.spend)}</span>
                <span>
                  Лиды: {int(p.leads)}
                  {p.cpl !== null && ` · CPL ${usd(p.cpl)}`}
                </span>
                <span>
                  Апрувы: {int(p.approved)} · выплаты {usd(p.revenue)}
                </span>
                <span className={p.profit >= 0 ? 'pos' : 'neg'}>Прибыль: {signedUsd(p.profit)}</span>
                {p.inProcess > 0 && <span>Прогноз: {signedUsd(p.forecastProfit)}</span>}
              </>
            );
          }}
        />
      </div>
    </Card>
  );
}
